import { createContext, useContext, useState, useRef, useEffect, useCallback, ReactNode } from 'react';
import type { MusicTrack } from '@/hooks/useMusicTracks';

interface PlayerContextType {
  currentTrack: MusicTrack | null;
  playlist: MusicTrack[];
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  isMuted: boolean;
  showFullscreen: boolean;
  setCurrentTrack: (track: MusicTrack | null) => void;
  setPlaylist: (tracks: MusicTrack[]) => void;
  togglePlay: () => void;
  play: () => void;
  pause: () => void;
  next: () => void;
  previous: () => void;
  seek: (time: number) => void;
  setVolume: (volume: number) => void;
  toggleMute: () => void;
  setShowFullscreen: (show: boolean) => void;
  audioRef: React.RefObject<HTMLAudioElement>;
}

const PlayerContext = createContext<PlayerContextType | null>(null);

export function PlayerProvider({ children }: { children: ReactNode }) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [currentTrack, setCurrentTrackState] = useState<MusicTrack | null>(null);
  const [playlist, setPlaylist] = useState<MusicTrack[]>([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolumeState] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [showFullscreen, setShowFullscreen] = useState(false);

  // Set current track and auto-play
  const setCurrentTrack = useCallback((track: MusicTrack | null) => {
    setCurrentTrackState(track);
    if (track) {
      setIsPlaying(true);
    }
  }, []);

  // Update audio when track changes
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !currentTrack) return;

    audio.src = currentTrack.url;
    audio.load();
    setCurrentTime(0);
    setDuration(currentTrack.duration);

    if (isPlaying) {
      audio.play().catch(console.error);
    }
  }, [currentTrack?.id]); // Only trigger when track ID changes

  // Audio event listeners
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration || currentTrack?.duration || 0);
    const handleEnded = () => {
      next();
    };
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
    };
  }, [currentTrack?.duration]);

  const togglePlay = useCallback(async () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      try {
        await audioRef.current.play();
      } catch (error) {
        console.error('Playback error:', error);
      }
    }
  }, [isPlaying]);

  const play = useCallback(async () => {
    if (!audioRef.current || isPlaying) return;
    try {
      await audioRef.current.play();
    } catch (error) {
      console.error('Playback error:', error);
    }
  }, [isPlaying]);

  const pause = useCallback(() => {
    if (!audioRef.current || !isPlaying) return;
    audioRef.current.pause();
  }, [isPlaying]);

  const next = useCallback(() => {
    if (!currentTrack || playlist.length === 0) return;
    
    // Find current track by ID
    const currentIndex = playlist.findIndex(t => t.id === currentTrack.id);
    if (currentIndex === -1) return;
    
    const nextIndex = (currentIndex + 1) % playlist.length;
    setCurrentTrack(playlist[nextIndex]);
  }, [currentTrack, playlist, setCurrentTrack]);

  const previous = useCallback(() => {
    if (!currentTrack || playlist.length === 0) return;
    
    // Find current track by ID
    const currentIndex = playlist.findIndex(t => t.id === currentTrack.id);
    if (currentIndex === -1) return;
    
    const prevIndex = currentIndex === 0 ? playlist.length - 1 : currentIndex - 1;
    setCurrentTrack(playlist[prevIndex]);
  }, [currentTrack, playlist, setCurrentTrack]);

  const seek = useCallback((time: number) => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = time;
    setCurrentTime(time);
  }, []);

  const setVolume = useCallback((vol: number) => {
    if (!audioRef.current) return;
    audioRef.current.volume = vol;
    setVolumeState(vol);
    setIsMuted(vol === 0);
  }, []);

  const toggleMute = useCallback(() => {
    if (!audioRef.current) return;
    if (isMuted) {
      audioRef.current.volume = volume;
      setIsMuted(false);
    } else {
      audioRef.current.volume = 0;
      setIsMuted(true);
    }
  }, [isMuted, volume]);

  return (
    <PlayerContext.Provider
      value={{
        currentTrack,
        playlist,
        isPlaying,
        currentTime,
        duration,
        volume,
        isMuted,
        showFullscreen,
        setCurrentTrack,
        setPlaylist,
        togglePlay,
        play,
        pause,
        next,
        previous,
        seek,
        setVolume,
        toggleMute,
        setShowFullscreen,
        audioRef,
      }}
    >
      <audio ref={audioRef} preload="metadata" />
      {children}
    </PlayerContext.Provider>
  );
}

export function usePlayer() {
  const context = useContext(PlayerContext);
  if (!context) {
    throw new Error('usePlayer must be used within PlayerProvider');
  }
  return context;
}
